/**
 * Crossword — sound palette.
 *
 * Aesthetic: a pencil on newsprint at a quiet table. Typing is a soft graphite
 * stroke (filtered noise, no pitched body); moving the cursor is a fainter
 * paper tick; erasing is a low scuff. Checking a word plays a calm pair of
 * notes — sage "that's right" rises, slate "look again" dips, neither punitive.
 * Finishing a word gives a small warm "set" chime; solving the whole grid pays
 * off with a four-note open-fifth chord that rings like a struck triangle.
 *
 * Bespoke (POLISH-GATE item 13): where Five sings warm triangle tones on a G/F/C
 * pentatonic, crossword leans on noise-bed graphite strokes and a brighter,
 * paper-thin pitched layer in a different key (A / E open fifths). Different
 * oscillators, different envelopes, different frequencies.
 *
 * Every public play* routes through `audio.playIfNotMuted` so the global mute
 * toggle is honored automatically.
 */

import { audio } from '@/juice';

import type { HapticStyle } from '@rendever/games-contracts';

export type CrosswordSoundName =
  | 'pencil'      // type a letter
  | 'move'        // move the cursor (arrow / focus)
  | 'erase'       // backspace
  | 'flip'        // toggle direction
  | 'wordDone'    // a word just filled completely
  | 'checkRight'  // check verdict — all correct
  | 'checkSoft'   // check verdict — something to look at again
  | 'reveal'      // reveal a cell / word / puzzle
  | 'solve'       // the grid is complete
  | 'whoosh';     // page / hub transition

export const soundHaptic: Partial<Record<CrosswordSoundName, HapticStyle>> = {
  pencil: 'light',
  move: 'light',
  erase: 'light',
  flip: 'light',
  wordDone: 'medium',
  checkRight: 'success',
  checkSoft: 'light',
  reveal: 'light',
  solve: 'success',
  whoosh: 'light',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * A graphite stroke — short shaped noise through a low-mid bandpass. No pitched
 * body, so it reads as "pencil" rather than "click." `bright` lifts the filter
 * for the cursor tick.
 */
function synthStroke(opts: {
  centerHz: number;
  durationMs: number;
  peak?: number;
  bright?: boolean;
}): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = opts.durationMs / 1000;
  const peak = opts.peak ?? 0.16;

  const buf = audio.noiseBuffer(ctx, { lengthMs: opts.durationMs + 20, shaping: opts.bright ? 1 : 1.6 });
  const src = ctx.createBufferSource();
  src.buffer = buf;

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(opts.centerHz, t);
  // A gentle downward sweep — the stroke "lands."
  bp.frequency.exponentialRampToValueAtTime(opts.centerHz * 0.7, t + dur);
  bp.Q.value = opts.bright ? 0.8 : 1.1;

  const { node: g } = audio.envelope(ctx, {
    attack: 0.002,
    decay: 0.02,
    sustain: 0.3,
    release: dur,
    peak,
  });
  audio.connectThrough(src, bp, g, master);
  src.start(t);
  src.stop(t + dur + 0.03);
}

/** A thin pitched "set" tone — a struck-paper bell, used when a word completes. */
function synthPing(opts: { freq: number; durationMs: number; peak?: number }): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = opts.durationMs / 1000;
  const peak = opts.peak ?? 0.14;

  // Two stacked sines an octave apart for a glassy, paper-thin body.
  const fundamental = audio.makeOscillator(ctx, { freq: opts.freq, type: 'sine' });
  const shimmer = audio.makeOscillator(ctx, { freq: opts.freq * 2.01, type: 'sine' });

  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);

  const sg = ctx.createGain();
  sg.gain.setValueAtTime(0.0001, t);
  sg.gain.exponentialRampToValueAtTime(peak * 0.3, t + 0.012);
  sg.gain.exponentialRampToValueAtTime(0.0001, t + dur * 0.7);

  audio.connectThrough(fundamental, g, master);
  audio.connectThrough(shimmer, sg, master);
  fundamental.start(t);
  fundamental.stop(t + dur + 0.05);
  shimmer.start(t);
  shimmer.stop(t + dur + 0.05);
}

/** A small two-note motif — `rise` true climbs (right), false dips (soft). */
function synthVerdict(rise: boolean): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t0 = ctx.currentTime;
  // A4 → E5 climbing for "right"; A4 → F#4 dipping for "look again."
  const notes = rise ? [440.0, 659.25] : [440.0, 369.99];
  notes.forEach((freq, i) => {
    const t = t0 + i * 0.14;
    const osc = audio.makeOscillator(ctx, { freq, type: 'triangle' });
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = rise ? 3200 : 1600;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(rise ? 0.15 : 0.13, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    audio.connectThrough(osc, lp, g, master);
    osc.start(t);
    osc.stop(t + 0.55);
  });
}

/** The solve payoff — an open-fifth, four-note rising figure with an octave ring. */
function synthSolve(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t0 = ctx.currentTime;
  // A3 - E4 - A4 - E5 — open fifths, the "morning bell" of a finished page.
  const figure = [220.0, 329.63, 440.0, 659.25];
  figure.forEach((freq, i) => {
    const t = t0 + i * 0.15;
    const osc = audio.makeOscillator(ctx, { freq, type: 'triangle' });
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2800;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.0);
    audio.connectThrough(osc, lp, g, master);
    osc.start(t);
    osc.stop(t + 1.05);
    // Octave shimmer on the final note.
    if (i === figure.length - 1) {
      const oct = audio.makeOscillator(ctx, { freq: freq * 2, type: 'sine' });
      const og = ctx.createGain();
      og.gain.setValueAtTime(0.0001, t);
      og.gain.exponentialRampToValueAtTime(0.09, t + 0.05);
      og.gain.exponentialRampToValueAtTime(0.0001, t + 1.5);
      audio.connectThrough(oct, og, master);
      oct.start(t);
      oct.stop(t + 1.55);
    }
  });
}

/** A soft paper whoosh for transitions. */
function synthWhoosh(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = 0.34;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 380, shaping: 1.5 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(600, t);
  bp.frequency.exponentialRampToValueAtTime(1900, t + dur);
  bp.Q.value = 0.6;
  const { node: g } = audio.envelope(ctx, {
    attack: 0.02,
    decay: 0.08,
    sustain: 0.6,
    release: dur,
    peak: 0.15,
  });
  audio.connectThrough(src, bp, g, master);
  src.start(t);
  src.stop(t + dur + 0.05);
}

// ---------------------------------------------------------------------------
// Concrete sounds
// ---------------------------------------------------------------------------

function synthPencil(): void {
  synthStroke({ centerHz: 1500, durationMs: 60, peak: 0.13 });
}
function synthMove(): void {
  synthStroke({ centerHz: 2600, durationMs: 34, peak: 0.07, bright: true });
}
function synthErase(): void {
  synthStroke({ centerHz: 760, durationMs: 110, peak: 0.13 });
}
function synthFlip(): void {
  // A doubled paper tick — the highlighter changing axis.
  synthStroke({ centerHz: 2000, durationMs: 40, peak: 0.08, bright: true });
  window.setTimeout(() => synthStroke({ centerHz: 1500, durationMs: 36, peak: 0.06, bright: true }), 38);
}
function synthWordDone(): void {
  synthPing({ freq: 587.33, durationMs: 360, peak: 0.13 }); // D5
}

// ---------------------------------------------------------------------------
// Public controller
// ---------------------------------------------------------------------------

class CrosswordSound {
  unlock(): void {
    audio.unlockAudio();
  }

  play(name: CrosswordSoundName): void {
    audio.playIfNotMuted(name, () => {
      audio.unlockAudio();
      switch (name) {
        case 'pencil':
          synthPencil();
          return;
        case 'move':
          synthMove();
          return;
        case 'erase':
          synthErase();
          return;
        case 'flip':
          synthFlip();
          return;
        case 'wordDone':
          synthWordDone();
          return;
        case 'checkRight':
          synthVerdict(true);
          return;
        case 'checkSoft':
          synthVerdict(false);
          return;
        case 'reveal':
          synthPing({ freq: 440.0, durationMs: 420, peak: 0.12 });
          return;
        case 'solve':
          synthSolve();
          return;
        case 'whoosh':
          synthWhoosh();
          return;
      }
    });
  }
}

export const sound = new CrosswordSound();
